import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Contract } from '../../models/Contract'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { ContractPage } from './ContractPage'

const viewWillAppear = async ({
    urlParams,
    props: { action },
}: PocketComponent<{ action: 'new' | 'edit' | 'copy' }>) => {
    const contract = await (async (): Promise<Contract | null> => {
        switch (action) {
            case 'new': {
                return new Contract({})
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                return (await RecordFactory.getInstance().loadContract(urlParams.id)) ?? null
            }
            case 'copy': {
                if (!urlParams.id) {
                    return null
                }
                const contract =
                    (await RecordFactory.getInstance().loadContract(urlParams.id)) ?? null
                if (contract === null) {
                    return null
                }
                return contract.clone({ id: undefined, name: undefined })
            }
        }
    })()

    if (!contract) {
        return new RecordNotFoundError()
    }

    return { contract, action }
}

const render = ({
    data,
    base: { logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { contract, action } = data

    const navigate = useNavigate()

    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] = useState(false)

    const handleSave = async (contractToSave: Contract) => {
        setIsSaving(true)

        const result = await RecordFactory.getInstance().sendContract(contractToSave)
        setIsSaving(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate('/settings/contracts')
    }

    const handleShowDeleteConfirmation = () => {
        setIsShowingDeleteConfirmation(true)
    }

    const handleDelete = async () => {
        const contractID = contract.id
        if (contractID === undefined) {
            return
        }

        setIsDeleting(true)

        const result = await RecordFactory.getInstance().deleteContract(contractID)
        setIsDeleting(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate('/settings/contracts')
    }

    return (
        <>
            <ContractPage
                contract={contract}
                isSaving={isSaving}
                isDeleting={isDeleting}
                onSave={handleSave}
                onClose={() => navigate('/settings/contracts')}
                onDelete={action === 'edit' ? handleShowDeleteConfirmation : undefined}
            />

            <ConfirmationModal
                isOpen={isShowingDeleteConfirmation}
                onClose={() => setIsShowingDeleteConfirmation(false)}
                title={`Delete ${contract.name}?`}
                size="small"
                confirmButtonText="Yes, delete"
                isDestructive
                onConfirm={handleDelete}
                shouldHideCancelButton
            >
                <Paragraph>This action cannot be undone.</Paragraph>
            </ConfirmationModal>
        </>
    )
}

export const ContractWorkflow = usePSComponent(render, viewWillAppear)
